import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { AppService } from 'app/app.service';
import { AccountFeature } from 'app/constants/constants';
import { WithAuditContext } from 'app/providers/http-mutating-operations.interceptor';
import { SemantriaAccountService } from 'app/providers/semantria-api/semantria-account.service';
import { SemantriaAPIService } from 'app/providers/semantria-api.service';
import { EnableClassTelemetry } from 'app/services/otel.service';
import { SystemService } from 'app/services/system.service';
import { AccountUtils } from 'app/utils/account.utils';

interface PurposeGenerationResponse {
  id: string;
  results: { text: string }[];
}

interface ResultText {
  summary: string;
}

@EnableClassTelemetry()
@Injectable({ providedIn: 'root' })
export class QueryTopicService extends SemantriaAPIService {
  private readonly DEFAULT_QUERY_TOPIC_PURPOSE_GENERATION_SCEANRIO_ID =
    '28cb91b3-7c7d-4f7a-85e8-9aeb1356961d';

  private readonly FEATURE_LLM: AccountFeature = 'LARGE_LANGUAGE_MODELS';
  private readonly EXTERNAL_RESOURCE_TYPE_SPOT_E_SCENARIO = 'SPOT_E_SCENARIO';

  constructor(
    _httpClient: HttpClient,
    _systemService: SystemService,
    private accountService: SemantriaAccountService
  ) {
    super(_httpClient, _systemService);
  }

  async hasPurposeGenerationPermission(): Promise<boolean> {
    const accountDetails = await this.accountService.sub.latest();
    return new Promise((resolve) =>
      resolve(
        AccountUtils.hasFeature(accountDetails, this.FEATURE_LLM) &&
          AccountUtils.hasExternalResource(
            accountDetails,
            AppService.getConfig().spote.queryTopicPurposeGenerationScenarioId ||
              this.DEFAULT_QUERY_TOPIC_PURPOSE_GENERATION_SCEANRIO_ID,
            undefined,
            this.EXTERNAL_RESOURCE_TYPE_SPOT_E_SCENARIO
          )
      )
    );
  }

  @WithAuditContext('query-topic-purpose-generated', (instance: QueryTopicService, args) => {
    return {
      query: args[0],
      scenario: instance.DEFAULT_QUERY_TOPIC_PURPOSE_GENERATION_SCEANRIO_ID,
    };
  })
  generatePurpose(query: string): Promise<string> {
    return this.post<PurposeGenerationResponse>(
      `/spote/scenarios/${this.DEFAULT_QUERY_TOPIC_PURPOSE_GENERATION_SCEANRIO_ID}/`,
      { query, purpose_char_min: 350, purpose_char_max: 512 },
      {
        observe: 'body',
      }
    ).then((response) => {
      if (response?.results && response.results.length > 0) {
        try {
          const responseJson: ResultText = JSON.parse(response.results[0].text);

          if (responseJson.summary) {
            return responseJson.summary;
          }

          throw new Error('Result not found in response');
        } catch {
          throw new Error('Response text is not a valid JSON');
        }
      }

      throw new Error('Result not found in response');
    });
  }
}
